import type { ComponentType } from "react";
import { Boxes, Cloud, Server } from "lucide-react";
import {
  siDocker,
  siGooglecloud,
  siHelm,
  siKubernetes,
  siLinux,
  siNginx,
  siProxmox,
  siRancher,
  siRedhat,
  siRedhatopenshift,
  siTerraform,
  siVmware,
} from "simple-icons";
import { Badge } from "./ui/Badge";
import { Reveal } from "./Reveal";

type Mark = { title: string; hex: string; path?: string };

const m = (i: { title: string; hex: string; path: string }): Mark => ({ title: i.title, hex: `#${i.hex}`, path: i.path });
// AWS & Azure are dropped from simple-icons (trademark) → branded label chips.
const AWS: Mark = { title: "AWS", hex: "#FF9900" };
const AZURE: Mark = { title: "Azure", hex: "#0078D4" };

interface Target {
  icon: ComponentType<{ className?: string }>;
  eyebrow: string;
  title: string;
  body: string;
  marks: Mark[];
}

const TARGETS: Target[] = [
  {
    icon: Server,
    eyebrow: "Air-gap ready",
    title: "On-premise",
    body: "Run the entire platform inside your own data center. Signed XML configs are portable and self-contained — nothing has to phone home.",
    marks: [m(siVmware), m(siProxmox), m(siLinux), m(siRedhat), m(siNginx)],
  },
  {
    icon: Cloud,
    eyebrow: "Your tenancy",
    title: "Private cloud",
    body: "Deploy into your own VPC on the cloud you already trust. Your data and secrets never leave your security boundary.",
    marks: [AWS, AZURE, m(siGooglecloud), m(siTerraform)],
  },
  {
    icon: Boxes,
    eyebrow: "Cloud-native",
    title: "Cloud containers",
    body: "Ship as containers and scale on Kubernetes or OpenShift. gVisor-isolated sandboxes run AI-generated code safely at any scale.",
    marks: [m(siDocker), m(siKubernetes), m(siRedhatopenshift), m(siHelm), m(siRancher)],
  },
];

function Glyph({ mark, i }: { mark: Mark; i: number }) {
  return (
    <span
      className="logo-float flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] shadow-lift backdrop-blur"
      style={{ animationDelay: `${i * 0.5}s` }}
      title={mark.title}
    >
      {mark.path ? (
        <svg viewBox="0 0 24 24" className="h-6 w-6" aria-label={mark.title}>
          <path d={mark.path} fill={mark.hex} />
        </svg>
      ) : (
        <span className="text-[11px] font-bold" style={{ color: mark.hex }}>
          {mark.title}
        </span>
      )}
    </span>
  );
}

export function DeployAnywhere() {
  return (
    <section id="deploy" className="relative scroll-mt-16 overflow-hidden bg-slate-950 py-24 text-slate-200">
      {/* ambient depth */}
      <div className="pointer-events-none absolute inset-0 bg-aurora opacity-40" />
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-accent-600/20 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-brand-500/20 blur-3xl" />

      <div className="relative mx-auto max-w-6xl px-6">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <Badge tone="violet" className="mx-auto border border-white/10 bg-white/10 text-accent-200">
              <Server className="h-3.5 w-3.5" /> Deploy anywhere
            </Badge>
            <h2 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Build with Wise 2.0. Deploy anywhere.
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-slate-400">
              One signed, portable artifact runs the same on your own hardware, in your private
              cloud, or as containers — so compliance, latency, and data-residency requirements are
              never a blocker.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {TARGETS.map((t, ti) => (
            <Reveal key={t.title} delay={ti * 100}>
              <div className="group flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-sm transition-colors hover:border-white/20">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-gradient text-white shadow-glow">
                    <t.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-accent-300">{t.eyebrow}</p>
                    <h3 className="text-lg font-semibold text-white">{t.title}</h3>
                  </div>
                </div>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-400">{t.body}</p>
                <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-white/10 pt-6">
                  {t.marks.map((mk, i) => (
                    <Glyph key={mk.title} mark={mk} i={i} />
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={120}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-slate-400">
            {["Same signed artifact everywhere", "No data leaves your boundary", "Air-gap & offline friendly", "Scales from a laptop to a cluster"].map((t) => (
              <span key={t} className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> {t}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
